import {createAsyncThunk, createSlice} from "@reduxjs/toolkit";
import api from "./api";
import {handleResponseStatus, updateIdField, updateItem} from "./utils";

const initialState = {
    items: [],
    fetchStatus: "idle",
    fetchByIdStatus: "idle",
    createStatus: "idle",
    updateStatus: "idle",
    deleteStatus: "idle",
    pagination: {},
    error: null,
};

// Fetch employees list
export const fetchAllDomain = createAsyncThunk('domain/fetchAllDomain',
    async () => {
        try {
            const response = await api.get(`/form-access`);
            return {...response};
        } catch (error) {
            if (error.response) return error.response;
            throw error;
        }
    }
);

// Create a new domain
export const createDomain = createAsyncThunk('domain/createDomain',
    async (data) => {
        try {
            const response = await api.post(`/form-access`, data);
            return {...response};
        } catch (error) {
            if (error.response) return error.response;
            throw error;
        }
    }
);

// Fetch domain by ID
export const fetchDomainById = createAsyncThunk('domain/fetchDomainById',
    async ({id}) => {
        try {
            const response = await api.get(`/form-access/${id}`);
            return {...response};
        } catch (error) {
            if (error.response) return error.response;
            throw error;
        }
    }
);

// Update domain
export const updateDomain = createAsyncThunk('domain/updateDomain',
    async ({id, data}) => {
        try {
            const response = await api.patch(`/form-access/${id}`, data, {
                useMultipart: true,
            });
            return {...response};
        } catch (error) {
            if (error.response) return error.response;
            throw error;
        }
    }
);

// Delete domain
export const deleteDomain = createAsyncThunk('domain/deleteDomain',
    async ({id}) => {
        try {
            const response = await api.delete(`/form-access/${id}`);
            return {...response};
        } catch (error) {
            if (error.response) return error.response;
            throw error;
        }
    }
);

const domainSlice = createSlice({
    name: "domain",
    initialState,
    reducers: {
        resetUpdateStatus(state) {
            state.updateStatus = 'idle';
        },
        resetDeleteStatus(state) {
            state.deleteStatus = 'idle';
        },
        resetCreateStatus(state) {
            state.createStatus = 'idle';
        },
    },
    extraReducers: (builder) => {
        builder
            // Fetch domain
            .addCase(fetchAllDomain.pending, (state) => {
                state.fetchStatus = 'loading';
            })
            .addCase(fetchAllDomain.fulfilled, (state, action) => {
                const {fetchStatus, error} = handleResponseStatus(action.payload, 'fetchStatus');
                const {data, ...pagination} = action.payload.data;
                state.items = fetchStatus === "success" ? updateIdField(data) : state.items;
                state.fetchStatus = fetchStatus;
                state.pagination = pagination;
                state.error = error;
            })
            .addCase(fetchAllDomain.rejected, (state, action) => {
                state.fetchStatus = 'failed';
                state.error = action.error.message;
            })

            // Create domain
            .addCase(createDomain.pending, (state) => {
                state.createStatus = 'loading';
            })
            .addCase(createDomain.fulfilled, (state, action) => {
                const {createStatus, error} = handleResponseStatus(action.payload, 'createStatus');
                state.createStatus = createStatus;
                state.error = error;
            })
            .addCase(createDomain.rejected, (state, action) => {
                state.createStatus = 'failed';
                state.error = action.error.message;
            })

            // Fetch domain by ID
            .addCase(fetchDomainById.pending, (state) => {
                state.fetchByIdStatus = 'loading';
            })
            .addCase(fetchDomainById.fulfilled, (state, action) => {
                const {fetchByIdStatus, error} = handleResponseStatus(action.payload, 'fetchByIdStatus');
                state.items = fetchByIdStatus === "success" ? updateItem(action.payload.data.data, state.items) : state.items;
                state.fetchByIdStatus = fetchByIdStatus;
                state.error = error;
            })
            .addCase(fetchDomainById.rejected, (state, action) => {
                state.fetchByIdStatus = 'failed';
                state.error = action.error.message;
            })

            // Update domain
            .addCase(updateDomain.pending, (state) => {
                state.updateStatus = 'loading';
            })
            .addCase(updateDomain.fulfilled, (state, action) => {
                const {updateStatus, error} = handleResponseStatus(action.payload, 'updateStatus');
                state.updateStatus = updateStatus;
                state.error = error;
            })
            .addCase(updateDomain.rejected, (state, action) => {
                state.updateStatus = 'failed';
                state.error = action.error.message;
            })

            // Delete domain
            .addCase(deleteDomain.pending, (state) => {
                state.deleteStatus = 'loading';
            })
            .addCase(deleteDomain.fulfilled, (state, action) => {
                const {deleteStatus, error} = handleResponseStatus(action.payload, 'deleteStatus');
                const id = action.meta.arg.id;
                if (deleteStatus === "success") {
                    state.items = state.items.filter(item => item.id !== id);
                }
                state.deleteStatus = deleteStatus;
                state.error = error;
            })
            .addCase(deleteDomain.rejected, (state, action) => {
                state.deleteStatus = 'failed';
                state.error = action.error.message;
            });
    }
});

export const {
    resetUpdateStatus,
    resetDeleteStatus,
    resetCreateStatus,
} = domainSlice.actions;

export default domainSlice.reducer;
